import React, { useState } from "react";
import { IconButton, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { useTranslation } from "react-i18next";
import PortugalFlag from "@Images/translation/portugal.png";
import UKFlag from "@Images/translation/reino-unido.png";
import SpainFlag from "@Images/translation/spain.png";
import GermanyFlag from "@Images/translation/germany.png";
import FranceFlag from "@Images/translation/france.png";
import ItalyFlag from "@Images/translation/italy.png";
import UkraineFlag from "@Images/translation/ukraine.png";
import { useSiteContent } from "../SiteContentContext";

const useStyles = makeStyles(() => ({
    iconFlag: {
        width: 25,
        height: 25,
        backgroundSize: "cover",
        objectFit: "cover"
    }
}));

const FLAGS: Record<string, { src: string; alt: string }> = {
    pt: { src: PortugalFlag, alt: "Portuguese" },
    en: { src: UKFlag, alt: "English" },
    es: { src: SpainFlag, alt: "Spanish" },
    de: { src: GermanyFlag, alt: "German" },
    fr: { src: FranceFlag, alt: "French" },
    it: { src: ItalyFlag, alt: "Italian" },
    uk: { src: UkraineFlag, alt: "Ukrainian" }
};

const LANGS = ["pt", "en", "es", "de", "fr", "it", "uk"];

// Native-name fallbacks so the menu reads correctly even before/without the
// CommonResources `languages.*` keys being loaded in the marketing bundle.
const NATIVE_NAMES: Record<string, string> = {
    pt: "Português",
    en: "English",
    es: "Español",
    de: "Deutsch",
    fr: "Français",
    it: "Italiano",
    uk: "Українська"
};

/**
 * Marketing-only language switcher. Same flag UX as the app's FlagMenu, but
 * i18n-only — no Redux dispatch — so the /fidava bundle stays free of the
 * app store. Persists the choice to the i18next localStorage key so it
 * survives reloads and carries into the app on first authenticated load.
 */
const SiteLanguageMenu: React.FC = () => {
    const { content } = useSiteContent();
    const languageLabel = (content as { header?: { a11y?: { language?: string } } }).header?.a11y?.language ?? "Language";
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const current = (i18n.language || "pt").split("-")[0];

    const flag = (code: string) => {
        const f = FLAGS[code] || FLAGS.en;
        return <img src={f.src} className={classes.iconFlag} alt={f.alt} loading="lazy" />;
    };

    const change = (lang: string) => {
        i18n.changeLanguage(lang);
        try { localStorage.setItem("i18nextLng", lang); } catch { /* ignore */ }
        setAnchorEl(null);
    };

    return (
        <div>
            <IconButton
                aria-controls="site-language-menu"
                aria-haspopup="true"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label={languageLabel}
            >
                {flag(current)}
            </IconButton>
            <Menu
                id="site-language-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {LANGS.map((lang) => (
                    <MenuItem key={lang} onClick={() => change(lang)}>
                        <ListItemIcon>{flag(lang)}</ListItemIcon>
                        {t(`languages.${lang}`, { defaultValue: NATIVE_NAMES[lang] })}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default React.memo(SiteLanguageMenu);
